import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import './InteractiveGlobe.css';

export default function InteractiveGlobe({ quiet = false }) {
  const { lang } = useParams();
  const ko = lang === 'ko';
  const host = useRef(null);
  const engine = useRef(null);
  const [status, setStatus] = useState('loading');
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let started = false;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const initialPause = media.matches || Boolean(navigator.connection?.saveData);
    setPaused(initialPause);
    let desiredPause = initialPause;
    let isVisible = true;
    const element = host.current;
    const observer = new IntersectionObserver(async ([entry]) => {
      isVisible = entry.isIntersecting;
      engine.current?.setVisible(isVisible);
      if (!entry.isIntersecting || started) return;
      started = true;
      try {
        const { createGlobe } = await import('./globe/globeScene');
        if (cancelled) return;
        engine.current = createGlobe(element, {
          quiet, paused: desiredPause,
          onFailure: () => { if (!cancelled) setStatus('fallback'); },
        });
        engine.current.setVisible(isVisible);
        setStatus('ready');
      } catch {
        if (!cancelled) setStatus('fallback');
      }
    }, { rootMargin: '80px' });
    observer.observe(element);
    const onMotionChange = () => {
      desiredPause = media.matches;
      setPaused(media.matches);
      engine.current?.setPaused(media.matches);
    };
    media.addEventListener('change', onMotionChange);
    return () => {
      cancelled = true;
      observer.disconnect();
      media.removeEventListener('change', onMotionChange);
      engine.current?.dispose();
      engine.current = null;
    };
  }, [quiet]);

  const toggle = () => {
    setPaused(value => {
      engine.current?.setPaused(!value);
      return !value;
    });
  };
  const label = ko ? '입체 모바일 플레이 조형물. 드래그 또는 방향키로 회전, Home 키로 초기화' : 'Interactive mobile playground. Drag or use arrow keys to rotate. Home resets the view.';
  return <div className={`interactive-globe ${quiet ? 'globe-quiet' : 'globe-hero'}`} data-state={status}>
    <div className="globe-aura" aria-hidden="true" />
    <div className="globe-fallback" aria-hidden="true"><span style={{backgroundImage: "url('/assets/playground-fallback.svg')"}} /><i /></div>
    <div className="globe-canvas" ref={host} tabIndex={status === 'ready' ? 0 : -1} role="group" aria-label={label} />
    <div className="globe-topline" aria-hidden="true"><span>EDGE / WORLD OF PLAY</span><span className="globe-live-dot" /></div>
    <div className="globe-controls">
      <span>{status === 'ready' ? (ko ? '드래그해서 플레이를 둘러보세요' : 'Drag to explore the playground') : 'FROM KOREA. FOR THE WORLD.'}</span>
      {status === 'ready' && <button type="button" onClick={toggle} aria-label={ko ? (paused ? '플레이 모션 재생' : '플레이 모션 일시정지') : (paused ? 'Play playground motion' : 'Pause playground motion')} aria-pressed={paused}>{paused ? '▶' : 'Ⅱ'}</button>}
    </div>
  </div>;
}
